import { useEffect, type MutableRefObject } from "react";
import { type FormState } from "@core/features/form";
import { type StepValidationState } from "../onboarding.types";

type UseStepFormSyncArgs = {
  /** Whether to sync state with parent (typically !showActionButtons or variant === "embedded") */
  shouldSync: boolean;
  /** Callback when step validation state changes */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Form state from react-hook-form or custom state */
  formState: FormState;
  /** Submit handler to expose to parent */
  onSubmit: () => Promise<void> | void;
  /**
   * Whether the step can proceed.
   * Defaults to: isDirty && isValid && !isSubmitting
   */
  canProceed?: boolean;
  /** Optional custom label for the primary button */
  primaryLabel?: string;
  /** Whether the step handles its own loading UI */
  handlesOwnLoading?: boolean;
};

/**
 * Hook to sync form state with parent onboarding container.
 *
 * Used by form containers (TimezoneFormContainer, ProfileFormContainer, etc.)
 * to report their validation state and expose submit handlers when the parent
 * is responsible for action buttons.
 *
 * @example
 * ```tsx
 * useStepFormSync({
 *   shouldSync: !showActionButtons,
 *   onStepStateChange,
 *   submitRef,
 *   formState: { isValid, isDirty, isSubmitting },
 *   onSubmit: handleSubmit,
 * });
 * ```
 */
export function useStepFormSync({
  shouldSync,
  onStepStateChange,
  submitRef,
  formState: { isValid, isDirty, isSubmitting },
  onSubmit,
  canProceed,
  primaryLabel,
  handlesOwnLoading,
}: UseStepFormSyncArgs) {
  // Default canProceed if not provided
  const resolvedCanProceed = canProceed ?? (isDirty && isValid && !isSubmitting);

  // Report validation state to parent
  useEffect(() => {
    if (shouldSync && onStepStateChange) {
      onStepStateChange({
        isValid,
        isDirty,
        isSubmitting,
        canProceed: resolvedCanProceed,
        primaryLabel,
        handlesOwnLoading,
      });
    }
  }, [
    shouldSync,
    isValid,
    isDirty,
    isSubmitting,
    resolvedCanProceed,
    primaryLabel,
    handlesOwnLoading,
    onStepStateChange,
  ]);

  // Expose submit handler to parent via ref
  useEffect(() => {
    if (shouldSync && submitRef) {
      submitRef.current = onSubmit;
    }
  }, [shouldSync, submitRef, onSubmit]);
}

export default useStepFormSync;
